// SF3 — Returns by Geography.
//
// THE REASON IS THE POINT. A regional spike on a damage or undeliverable reason
// is a lane or packaging problem; the same spike on a fit or expectation reason
// is a listing problem. Those have entirely different fixes, and nothing else in
// Seller Intelligence separates them — so the reason sits on the row rather than
// behind a click.
//
// Administrative geography here (state, city, pincode), matching Geographic
// Sales Intelligence — a return follows the sale. The delivery tracker above is
// on postal geography, which follows courier routing instead.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../GeographicOrders.module.css';
import { getReturnsGeography } from '../services/geographicOrdersApi';

const GRAINS = [
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// Reasons that mean the parcel never reached the buyer. These are a delivery
// problem wearing a returns label, and they want a different fix from a product
// the buyer received and did not want.
const DELIVERY_REASONS = /UNDELIVERABLE|REFUSED|MISSED_ESTIMATED_DELIVERY|NO_LONGER_NEEDED_LATE/i;

const Blank = ({ title }) => <span className={local.blank} title={title}>—</span>;

const ReturnsTable = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [grain, setGrain] = useState('state');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getReturnsGeography(token, { start, end, grain }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, grain, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Where returns come from, and why</h2>
        <div className={local.segmented}>
          {GRAINS.map((option) => (
            <button key={option.key} type="button"
              className={option.key === grain ? local.segmentOn : local.segment}
              onClick={() => setGrain(option.key)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className={local.muted}>
        Ranked by rate, not volume — a raw count would put your biggest regions
        on top every time whether or not they have a problem. Returns count
        against the period of the original order, not the day they came back.
      </p>

      {/* Returns lag the sale, so the newest orders have not finished
          returning. Saying so beats letting the latest period look best. */}
      {data?.maturing && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>The most recent weeks are still maturing</strong>
            <div>
              Orders placed inside the return window have not finished returning,
              so their rate is provisional and will rise.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load returns'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Region</th>
                <th className={local.numeric}>Returned</th>
                <th className={local.numeric}>Value</th>
                <th className={local.numeric}>Sold</th>
                <th className={local.numeric}>Rate</th>
                <th className={local.numeric}>Share</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={7}>
                    {loading ? 'Loading…' : 'No returns in this period.'}
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.region}>
                  <td className={row.unassigned ? local.unassigned : undefined}>
                    {row.region}
                  </td>
                  <td className={local.numeric}>{inr.format(row.units_returned)}</td>
                  <td className={local.numeric}>₹{inr.format(Math.round(row.return_value))}</td>
                  <td className={local.numeric}>{inr.format(row.units_sold)}</td>
                  <td className={local.numeric}>
                    {row.return_rate === null
                      ? <Blank title={`Only ${row.units_sold} units sold — too few for a rate`} />
                      : <span className={row.return_rate >= 0.08 ? local.rateHigh : undefined}>
                        {(row.return_rate * 100).toFixed(1)}%
                      </span>}
                  </td>
                  <td className={local.numeric}>
                    {row.share_of_returns === null ? <Blank />
                      : `${(row.share_of_returns * 100).toFixed(1)}%`}
                  </td>
                  <td>
                    <div className={local.reasonRow}>
                      {row.top_reasons.map((reason) => (
                        <span key={reason.reason}
                          className={DELIVERY_REASONS.test(reason.reason)
                            ? local.reasonDelivery : local.reasonChip}
                          title={DELIVERY_REASONS.test(reason.reason)
                            ? 'Never reached the buyer — a delivery or lane problem'
                            : 'Came back after delivery — a product or listing problem'}>
                          {reason.label} {reason.units}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.totals && (
        <div className={local.coverage}>
          {inr.format(data.totals.units_returned)} units returned worth
          ₹{inr.format(Math.round(data.totals.return_value))} against
          {' '}{inr.format(data.totals.units_sold)} sold, across
          {' '}{data.regions} regions. Amber reasons never reached the buyer —
          those are a delivery problem, not a product one.
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ReturnsTable;
